import {
  ConflictChangeCountsQueryCgResponse,
  ConflictCheckQueryCgResponse,
  ConflictSkillCoverageQueryCgResponse,
  LoadBalancingConflictCheckState,
  LoadBalancingConflictResolution,
  LoadBalancingConflictSituation,
  LoadBalancingConflictStatus,
  LoadBalancingScheduleNeedsAttentionHighlightTone,
  ScheduleReviewQueryCgResponse,
} from '@common/graphql';

// NOTE: default only at the GraphQL read sites (review + team-review pages, worker drawer);
//   every component and helper below takes chargeRoleTerm as a required prop.
export const DEFAULT_CHARGE_ROLE_TERM = 'Charge';

// NOTE: clinical roles are a per-service-line configured set (the server sends
// `Role#abbreviation`), so this stays an open string and never a closed union.
export type ScheduleRoleType = string;

export type ScheduleShiftTypeSectionKeyType = 'day' | 'evening' | 'night';

export type ScheduleCodeTypeType =
  | 'shift'
  | 'pto'
  | 'education'
  | 'orientation'
  | 'precepting';

export type ScheduleShiftLaneType = 'role' | 'learning_and_development';

export type ScheduleShiftTypeSectionType = {
  key: ScheduleShiftTypeSectionKeyType;
  label: string;
  time: string;
  icon: 'sun' | 'sunset' | 'moon';
};

export type ScheduleShiftSourceType =
  | 'pattern'
  | 'self'
  | 'float'
  | 'pto'
  | 'open'
  // NOTE: a captured vendor shift Works could not attribute. The slot is staffed, so it counts as
  // coverage, but it carries no worker name and nobody to drag.
  | 'unmatched'
  | 'manual'
  | 'extra'
  | 'balancer'
  | 'ukg';

export type ScheduleAssignedReasonType = {
  key: string;
  label: string;
  hard: boolean;
  weight: number | null;
};

export type ScheduleShiftType = {
  id: string;
  dayKey: string;
  shiftTypeSection: ScheduleShiftTypeSectionKeyType;
  role: ScheduleRoleType;
  codeType?: ScheduleCodeTypeType;
  protected?: boolean;
  lane?: ScheduleShiftLaneType;
  coverage?: boolean;
  isChargeRole?: boolean;
  startHour: number;
  startTime?: string;
  endTime?: string;
  timeRange?: string;
  leadingHandoffRange?: string;
  trailingHandoffRange?: string;
  fteContributionHours?: number;
  workerName?: string;
  workerSortName?: string;
  createdAt?: string;
  assignedWorkerId?: string;
  source: ScheduleShiftSourceType;
  ukgConflictLabel?: string;
  allDay?: boolean;
  timeOff?: boolean;
  syntheticOverlay?: boolean;
  suggested?: boolean;
  fixedCommitment?: boolean;
  payCodeEdit?: boolean;
  scheduleCodeLabel?: string;
  assignedReasons?: ScheduleAssignedReasonType[];
  requiredQualifications?: string[];
};

export type ScheduleCandidateSignalKind =
  | 'positive'
  | 'caution'
  | 'blocking'
  | 'neutral';

export type ScheduleCandidateSignalType = {
  kind: ScheduleCandidateSignalKind;
  label: string;
};

export type ScheduleCandidateKpiType = {
  coverage: { before: number; after: number; target: number };
  fteRemaining: { before: number; after: number };
};

export type ScheduleDragSourceType = 'fit' | 'roster' | 'grid';

export type ScheduleDragStateType = {
  workerId: string;
  workerName: string;
  source: ScheduleDragSourceType;
  fromShiftId?: string;
} | null;

export type ScheduleDropWarningType = {
  kind: 'over_fte';
  label: string;
};

export type ScheduleDropValidityType = {
  valid: boolean;
  reason?: string;
  warning?: ScheduleDropWarningType;
  silent?: boolean;
};

export type ScheduleShiftDecisionType = 'accepted' | 'rejected';

export type ScheduleShiftOverrideType = {
  assignedMemberId?: string | null;
  suggestionDecision?: ScheduleShiftDecisionType;
};

export type ScheduleCellRefType = {
  dayKey: string;
  shiftTypeSection: string;
  role: string;
};

export type ScheduleDemandType = {
  dayKey: string;
  shiftTypeSection: ScheduleShiftTypeSectionKeyType;
  role: ScheduleRoleType;
  plannedCount: number;
};

export type ScheduleHolidayType = NonNullable<
  NonNullable<
    ScheduleReviewQueryCgResponse['scheduleReview']
  >['weeks'][number]['days'][number]['holiday']
>;

export type ScheduleDayType = {
  key: string;
  date: string;
  dayLabel: string;
  dateLabel: string;
  weekend?: boolean;
  holiday?: ScheduleHolidayType | null;
  // NOTE: the server's Sun-Sat week this day belongs to, not the frame showing it: a pay-week
  // frame draws days from two of them, so anything keyed by week reads this off the day.
  weekKey?: string;
};

export type ScheduleWeekStatusType = {
  key: string;
  label: string;
  status: 'pending' | 'complete' | 'empty';
  pendingCount: number;
  hasCoverageGap?: boolean;
};

export type ScheduleShiftTypeFilterType =
  | ScheduleShiftTypeSectionKeyType
  | 'all';

export type ScheduleMoveKindType =
  | 'correction'
  | 'assignment'
  | 'reassignment'
  | 'removal'
  | 'shift_cancellation'
  | 'shift_addition';

export type ScheduleMoveSourceType = 'system' | 'manual';

export type ScheduleMoveStatusType = 'accepted' | 'rejected' | 'superseded';

export type ScheduleMoveType = {
  id: string;
  kind: ScheduleMoveKindType;
  source: ScheduleMoveSourceType;
  status: ScheduleMoveStatusType;
  extra?: boolean;
  workerName?: string;
  fromShiftId?: string;
  toShiftId?: string;
  cellId?: string;
  timeRange?: string;
  displacedWorkerName?: string;
  reasons: string[];
  coverage?: { before: number; after: number; target: number };
  fteRemaining?: { before: number; after: number };
  createdAt: string;
  orderKey: string;
  editedBy?: string;
};

export type ScheduleMoveSourceFilterType = 'all' | 'system' | 'manual';

export type ScheduleEmploymentType = 'core' | 'agency';

export type ScheduleTeamShiftType = 'day' | 'night' | 'either';

export type ScheduleTeamShiftPatternType =
  | 'consecutive'
  | 'non_consecutive'
  | 'no_preference';

export type ScheduleTeamMemberTermType = {
  effectiveStartDate: string;
  effectiveEndDate?: string | null;
  role?: string | null;
  chargeEligible: boolean;
  active: boolean;
  homeUnitMember: boolean;
};

export type ScheduleTeamMemberType = {
  id: string;
  name: string;
  role: ScheduleRoleType;
  unit: string;
  employmentType: ScheduleEmploymentType;
  shiftType: ScheduleTeamShiftType;
  shiftPattern: ScheduleTeamShiftPatternType;
  chargeEligible: boolean;
  terms: ScheduleTeamMemberTermType[];
  homeUnitMember: boolean;
  ftePercentage: number | null;
  fteHours: number | null;
  scheduledHours: number | null;
  remainingHours: number | null;
  // NOTE: required, not optional. Every construction site has to state it, or a mapper that
  // rebuilds a member field by field drops it and the whole band reads as on-target.
  periodFteStatus: 'over' | 'under' | null;
  active: boolean;
  weekendCount: number;
  weekendTarget: number | null;
  weekendWillingness?: boolean | null;
  weeklyHours?: ScheduleTeamMemberWeekHoursType[];
  // NOTE: absent when the org does not re-anchor its week, and wherever weeklyHours is absent,
  // so a surface in Pay week mode falls back to the calendar breakdown rather than emptying.
  payWeeklyHours?: ScheduleTeamMemberWeekHoursType[];
  externalHeldSlots: ScheduleExternalHeldSlotType[];
  skills?: { id: string; name: string }[];
  availabilitySlots?: ScheduleAvailabilitySlotType[];
};

// NOTE: 'pay' re-anchors the grid onto the org's configured pay week; 'calendar' is the Sun-Sat
// default, and the only mode an org without a configured pay week ever sees.
export type WeekModeType = 'calendar' | 'pay';

export type ScheduleTeamMemberWeekHoursType = {
  // NOTE: matches weeks[].key on weeklyHours only; payWeeklyHours keys its own chunk, so a "w1"
  // in each names a different range. Join on startDate, never on this.
  weekKey: string;
  weekLabel: string;
  // NOTE: ISO date of the week's first day, and the join key between a grid frame and its hours.
  startDate: string;
  scheduledHours: number;
  targetHours: number;
};

export type ScheduleExternalHeldSlotType = {
  date: string;
  shiftTypeSection: ScheduleShiftTypeSectionKeyType;
};

export type ScheduleAvailabilityKindType =
  | 'available'
  | 'preferred_available'
  | 'preferred_unavailable'
  | 'unavailable';

// NOTE: the server derives the signal from the code's constraint_type, never from the kind's
// name, so an org that leaves Unavailable informational reads it as neutral, not blocked.
export type ScheduleAvailabilitySignalType =
  | 'positive'
  | 'prefers_off'
  | 'blocked'
  | 'neutral';

export type ScheduleAvailabilitySlotType = {
  date: string;
  shiftTypeSection: ScheduleShiftTypeSectionKeyType;
  kind: ScheduleAvailabilityKindType | null;
  signal: ScheduleAvailabilitySignalType;
  timeRange: string | null;
  allDay: boolean;
};

export type ScheduleOpenCellCandidateCountsType = {
  dayKey: string;
  shiftTypeSection: ScheduleShiftTypeSectionKeyType;
  role: ScheduleRoleType;
  eligible: number;
  available: number;
  preferredAvailable?: number;
  eligibleWorkerIds?: string[];
};

export type ScheduleTeamRoleFilterType = ScheduleRoleType | 'all';

export type ScheduleTeamFteFilterType = 'all' | 'below' | 'above';

export type ScheduleTeamUnitFilterType = string;

export type ConflictCheckType = NonNullable<
  ConflictCheckQueryCgResponse['conflictCheck']
>;

export type ConflictItemType = ConflictCheckType['conflicts'][number];

export type UkgAdditionItemType = ConflictCheckType['additions'][number];

export type ConflictShiftSideType = NonNullable<ConflictItemType['worksShift']>;

export type ConflictChangeCountsType = NonNullable<
  NonNullable<
    ConflictChangeCountsQueryCgResponse['conflictCheck']
  >['changeCounts']
>;

export type ConflictCoverageSummaryType = NonNullable<
  NonNullable<
    ConflictSkillCoverageQueryCgResponse['conflictCheck']
  >['skillCoverage']
>;

export type ConflictSituationType = LoadBalancingConflictSituation;

export type ConflictResolutionType = LoadBalancingConflictResolution;

export type ConflictCheckStateType = LoadBalancingConflictCheckState;

export type ConflictStatusType = LoadBalancingConflictStatus;

export type ScheduleAttentionToneType =
  LoadBalancingScheduleNeedsAttentionHighlightTone;
